import React, { Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { useAuth } from "./context/AuthContext";
import { useInteractionAnalytics } from "./hooks/useInteractionAnalytics";

import AuthLayout from "./components/layout/routing/AuthLayout";
import RequireAuth from "./components/layout/routing/RequireAuth";
import RequireRole from "./components/layout/routing/RequireRole";
import EvaluationGate from "./components/layout/routing/EvaluationGate";
import ConsentGate from "./components/layout/routing/ConsentGate";
import AppShell from "./components/layout/routing/AppShell";
import RoleRedirect from "./components/layout/routing/RoleRedirect";

import LoginRoute from "./pages/auth/LoginRoute";
import SignupTypeRoute from "./pages/auth/SignupTypeRoute";
import AthleteSignupRoute from "./pages/auth/AthleteSignupRoute";
import ScoutSignupRoute from "./pages/auth/ScoutSignupRoute";
import ForgotPasswordRoute from "./pages/auth/ForgotPasswordRoute";
import ResetPasswordRoute from "./pages/auth/ResetPasswordRoute";
import ConsentRoute from "./pages/auth/ConsentRoute";

import OnboardingRoute from "./pages/OnboardingRoute";
import AthleteDashboardPage from "./pages/AthleteDashboardPage";
import AthleteProfileRoute from "./pages/AthleteProfileRoute";
import NutritionRoute from "./pages/NutritionRoute";
import StatusUpdateRoute from "./pages/StatusUpdateRoute";
import ContactDataRoute from "./pages/ContactDataRoute";
import AthleteContactRequestsRoute from "./pages/AthleteContactRequestsRoute";

import ScoutDashboardRoute from "./pages/ScoutDashboardRoute";
import RecruiterSummaryRoute from "./pages/RecruiterSummaryRoute";
import RecruiterProfileRoute from "./pages/RecruiterProfileRoute";
import SearchesListRoute from "./pages/SearchesListRoute";
import AthleteSearchRoute from "./pages/AthleteSearchRoute";
import CoachAthleteDetailRoute from "./pages/CoachAthleteDetailRoute";
import MyAthletesPage from "./features/academy/MyAthletesPage";
import MessagesRoute from "./pages/MessagesRoute";
import SettingsPage from "./pages/SettingsPage";
import GuardianReviewRoute from "./pages/guardian/GuardianReviewRoute";
import KycGate from "./components/layout/routing/KycGate";
import KycRoute from "./pages/KycRoute";
import AthleteReportsPage from "./features/reports/pages/AthleteReportsPage";
import ScoutReportsPage from "./features/reports/pages/ScoutReportsPage";
import ScoutAthleteReportPage from "./features/reports/pages/ScoutAthleteReportPage";

// Lazy-load the ENTIRE /admin/* tree as its own chunk. AdminApp pulls in the
// heavy admin pages (dashboard charts, AthleteForm reuse), so end-user web and
// Capacitor mobile bundles never download admin code — it only loads for an
// authenticated admin who reaches an /admin route. This is the bundle-hygiene
// half of the spec (§5.1); the real security boundary is the backend.
const AdminApp = React.lazy(() => import("./pages/admin/AdminApp"));

const AdminFallback = () => (
  <div className="flex h-full flex-1 items-center justify-center bg-gray-50">
    <Loader2 className="animate-spin text-blue-900" size={32} />
  </div>
);

const App = () => {
  const location = useLocation();
  const { authChecked, isAuthenticated, userRole, authUserId } = useAuth();

  // Single global analytics init (as the old App.jsx hub had): PostHog
  // autocapture + $pageview, active only while authenticated. No-op unless
  // REACT_APP_POSTHOG_KEY is set.
  useInteractionAnalytics({
    currentView: location.pathname,
    userRole,
    userId: authUserId,
    isActive: authChecked && isAuthenticated,
  });

  return (
    <Routes>
    <Route path="/guardian/review" element={<GuardianReviewRoute />} />

    <Route element={<AuthLayout />}>
      <Route path="/login" element={<LoginRoute />} />
      <Route path="/login/athlete" element={<Navigate to="/login" replace />} />
      <Route path="/login/scout" element={<Navigate to="/login" replace />} />
      <Route path="/signup" element={<SignupTypeRoute />} />
      <Route path="/signup/athlete" element={<AthleteSignupRoute />} />
      <Route path="/signup/scout" element={<ScoutSignupRoute />} />
      <Route path="/forgot-password" element={<ForgotPasswordRoute />} />
      <Route path="/reset-password" element={<ResetPasswordRoute />} />
    </Route>

    <Route element={<RequireAuth />}>
      <Route path="/" element={<RoleRedirect />} />

      <Route element={<RequireRole role="athlete" />}>
        <Route path="/athlete/consent" element={<ConsentRoute />} />
        <Route element={<ConsentGate />}>
          <Route path="/athlete/onboarding" element={<OnboardingRoute />} />
          <Route element={<EvaluationGate />}>
            <Route element={<AppShell />}>
              <Route
                path="/athlete/dashboard"
                element={<AthleteDashboardPage />}
              />
              <Route
                path="/athlete/profile/personal"
                element={
                  <AthleteProfileRoute initialTab="personal" formMode="personal" />
                }
              />
              <Route
                path="/athlete/profile/training"
                element={
                  <AthleteProfileRoute
                    initialTab="entrenamiento"
                    formMode="exercises"
                  />
                }
              />
              <Route path="/athlete/nutrition" element={<NutritionRoute />} />
              <Route
                path="/athlete/status-update"
                element={<StatusUpdateRoute />}
              />
              {/* /athlete/social (Comunidad) desactivada en v1: la pantalla usa
                  datos de `data/mockData`. Ver nota en Sidebar.jsx. */}
              <Route
                path="/athlete/contact-requests"
                element={<AthleteContactRequestsRoute />}
              />
              <Route path="/athlete/messages" element={<MessagesRoute />} />
              <Route path="/athlete/messages/:contactRequestId" element={<MessagesRoute />} />
              <Route path="/athlete/reportes" element={<AthleteReportsPage />} />
              <Route path="/athlete/settings" element={<SettingsPage />} />
            </Route>
          </Route>
        </Route>
      </Route>

      <Route element={<RequireRole role="scout" />}>
        <Route path="/scout/kyc" element={<KycRoute />} />
        <Route element={<KycGate />}>
        <Route element={<AppShell />}>
          <Route path="/scout/dashboard" element={<ScoutDashboardRoute />} />
          <Route path="/scout/profile" element={<RecruiterProfileRoute />} />
          <Route path="/scout/my-athletes" element={<MyAthletesPage />} />
          <Route path="/scout/athletes" element={<RecruiterSummaryRoute />} />
          <Route
            path="/scout/athletes/:athleteId"
            element={<CoachAthleteDetailRoute />}
          />
          <Route path="/scout/searches" element={<SearchesListRoute />} />
          <Route
            path="/scout/searches/:searchId"
            element={<AthleteSearchRoute />}
          />
          <Route path="/scout/contact" element={<ContactDataRoute />} />
          <Route path="/scout/messages" element={<MessagesRoute />} />
          <Route path="/scout/messages/:contactRequestId" element={<MessagesRoute />} />
          <Route path="/scout/reportes" element={<ScoutReportsPage />} />
          <Route path="/scout/reportes/:athleteId" element={<ScoutAthleteReportPage />} />
          <Route path="/scout/settings" element={<SettingsPage />} />
        </Route>
        </Route>
      </Route>

      {/* Admin: no Consent/Evaluation/Kyc gates. The whole subtree is one
          lazy chunk rendered inside the shared AppShell (admin nav sections). */}
      <Route element={<RequireRole role="admin" />}>
        <Route element={<AppShell />}>
          <Route
            path="/admin/*"
            element={
              <Suspense fallback={<AdminFallback />}>
                <AdminApp />
              </Suspense>
            }
          />
        </Route>
      </Route>
    </Route>

    <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default App;
